import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();

  const userName =
    userProfile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.email?.split('@')?.[0] ||
    'User';

  const userEmail = userProfile?.email || user?.email || 'Not available';
  const userTier = userProfile?.tier || 'free';
  const creditsRemaining =
    userProfile?.credits_remaining ??
    (userTier === 'free' ? 0 : null);

  const navigationItems = [
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'New Analysis', path: '/resume-upload-analysis', icon: 'Upload' },
    { label: 'Skill Analysis', path: '/skill-gaps', icon: 'Target' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const handleNavigation = (item) => {
    if (item?.premium && userTier === 'free') {
      setIsUpgradeModalOpen(true);
      return;
    }
    navigate(item?.path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
    setIsUserMenuOpen(false);
  };

  const handleUpgrade = () => {
    navigate('/upgrade');
    setIsUpgradeModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-100">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-foreground">JobAspirant</span>
            </div>
          </div>

          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  } ${item?.premium && userTier === 'free' ? 'relative' : ''}`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
                {item?.premium && userTier === 'free' && (
                  <Icon name="Crown" size={12} className="text-warning" />
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={16} color="white" />
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium text-foreground">{userName}</div>
                  <div className="text-xs text-muted-foreground flex items-center space-x-1">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${userTier === 'premium'
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-warning text-warning-foreground'
                        }`}
                    >
                      {userTier === 'premium' ? 'Premium' : 'Free'}
                    </span>
                    {typeof creditsRemaining === 'number' && (
                      <span>
                        • {creditsRemaining} scan{creditsRemaining === 1 ? '' : 's'} left
                      </span>
                    )}
                    {creditsRemaining === 0 && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent parent button from triggering
                          navigate('/upgrade');
                        }}
                        className="text-xs text-primary underline ml-1 cursor-pointer"
                      >
                        Top up
                      </span>
                    )}
                  </div>
                </div>
                <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-medium animate-fade-in">
                  <div className="p-4 border-b border-border">
                    <div className="text-sm font-medium text-popover-foreground">{userName}</div>
                    <div className="text-xs text-muted-foreground">{userEmail}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${userTier === 'premium'
                          ? 'bg-accent text-accent-foreground'
                          : 'bg-warning text-warning-foreground'
                          }`}
                      >
                        {userTier === 'premium' ? 'Premium Plan' : 'Free Plan'}
                      </span>
                      {typeof creditsRemaining === 'number' && (
                        <span className="text-xs text-muted-foreground">
                          {creditsRemaining} credit{creditsRemaining === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    {creditsRemaining === 0 && (
                      <button
                        onClick={() => {
                          navigate('/upgrade');
                          setIsUserMenuOpen(false);
                        }}
                        className="mt-3 text-xs text-primary underline"
                      >
                        Top up credits
                      </button>
                    )}
                  </div>
                  <div className="p-2">
                    {userTier === 'free' && (
                      <button
                        onClick={() => {
                          setIsUpgradeModalOpen(true);
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-muted rounded-md transition-colors duration-200"
                      >
                        <Icon name="Crown" size={16} className="text-warning" />
                        <span>Upgrade to Premium</span>
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/user-dashboard')}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-muted rounded-md transition-colors duration-200"
                    >
                      <Icon name="Settings" size={16} />
                      <span>Account Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-left hover:bg-muted rounded-md transition-colors duration-200 text-error"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-card border-t border-border animate-slide-in">
            <nav className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {item?.premium && userTier === 'free' && (
                    <Icon name="Crown" size={14} className="text-warning ml-auto" />
                  )}
                </button>
              ))}
            </nav>
          </div>
        )}
      </header>

      {isUpgradeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
          <div className="bg-card rounded-lg shadow-prominent max-w-md w-full animate-scale-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-card-foreground">Upgrade to Premium</h3>
                <button
                  onClick={() => setIsUpgradeModalOpen(false)}
                  className="p-1 hover:bg-muted rounded-md transition-colors duration-200"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="text-center">
                  <Icon name="Crown" size={48} className="text-warning mx-auto mb-3" />
                  <p className="text-muted-foreground mb-4">
                    Unlock advanced features to accelerate your career growth
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Unlimited resume analyses</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Personalized learning paths</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">AI-powered resume rewriter</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-success" />
                    <span className="text-sm">Priority support</span>
                  </div>
                </div>

                <div className="text-center py-4">
                  <div className="text-2xl font-bold text-card-foreground">₹999</div>
                  <div className="text-sm text-muted-foreground">per month</div>
                </div>

                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setIsUpgradeModalOpen(false)} className="flex-1">
                    Maybe Later
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleUpgrade}
                    className="flex-1"
                    iconName="Crown"
                    iconPosition="left"
                  >
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;